import Button from '@cimpress/react-components/lib/Button';
import RadioGroup from '@cimpress/react-components/lib/RadioGroup';
import Radio from '@cimpress/react-components/lib/Radio';
import Select from '@cimpress/react-components/lib/Select';
import Table from '@cimpress/react-components/lib/Table';
import TextField from '@cimpress/react-components/lib/TextField';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import memoizeOne from 'memoize-one';
import { css } from 'emotion';
import get from 'lodash/get';
import { withTranslation } from 'react-i18next';
import BasicLayout from '../layouts/BasicLayout';
import createContractColumns from './contractTable/TableColumns';
import TableCards from './TableCards';
import { actions as termActions, selectors as termSelectors } from '../../reducers/terms';
import { selectors as identitySelectors } from '../../reducers/identities';
import { selectors as configSelectors } from '../../reducers/configurations';
import {
  updateTypeDispatch,
  resetTermsDispatch,
  toggleMultiselectDispatch,
  updateSellerIdDispatch,
  resetSkusDispatch,
  resetStateDispatch
} from '../../reducers/termselect/actions';
import { TermType } from '../../constants/termTypes';
import FilterOptions from './FilterOptions';
import { getLabelForContractType } from '../../utilities/displayFunctions';
import TermStatus from '../../constants/termStatus';
import { selectors as homeSelectors } from '../../reducers/home';
import isEmpty from 'lodash/isEmpty';
import BreadcrumbsEntry from '../common/breadcrumbs/BreadcrumbsEntry';
import { Toggle } from '@cimpress/react-components';

const tableStyle = css({
  '.rt-table': {
    overflow: 'auto'
  }
});

const toggleContainerStyle = css({
  display: 'flex',
  marginLeft: 'auto',
  alignItems: 'center',
  alignContent: 'center',
  padding: '10px 0px 30px 0px'
});

const termSelectOptionStyle = css({
  display: 'flex'
});

const toggleLabelStyle = css({
  paddingRight: '10px'
});

const sellerSelectStyle = css({
  minWidth: '300px'
});

const sellerSelectContainerStyle = css({
  paddingTop: '10px',
  paddingLeft: '50px'
});

const updateButtonStyle = css({
  padding: '10px 0px'
});

class HomePage extends Component {
  static propTypes = {
    terms: PropTypes.arrayOf(PropTypes.object).isRequired,
    authorizedMerchantIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    filterText: PropTypes.string,
    cardFilter: PropTypes.string,
    isPageLoading: PropTypes.bool,
    fetchTermsForHomePage: PropTypes.func.isRequired,
    setHomePageFilterText: PropTypes.func.isRequired
  };

  state = {
    cardFilter: '',
    data: [],
    searchText: '',
    effectiveTerms: [],
    bulkTermStatusUpdating: false
  };

  componentDidMount() {
    const { history, resetSkus, resetState, updateStatusList } = this.props;

    const cardFilter = get(history, 'location.state.cardFilter');
    cardFilter && this.setState({ cardFilter });

    this.props.fetchTermsForHomePage();

    resetSkus();
    // If returning to the home page from bulkUpdate through a breadcrumb link, clear out all terms and skus
    if (updateStatusList && updateStatusList.length) {
      resetState();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.newTerms.length !== this.props.newTerms.length) {
      this.props.fetchTermsForHomePage();
    }
  }

  createContractColumns = memoizeOne(({ t, selectMultipleTerms }) => createContractColumns({ t, selectMultipleTerms }));

  createFulfillerSelectOptions = memoizeOne((terms, sellerList, termType) => {
    // Find only the sellers in the current available terms
    const termSellerMap = terms.reduce((sellerMap, term) => {
      const { seller, type } = term;
      if (type === termType) sellerMap[seller.id] = seller;
      return sellerMap;
    }, {});

    // Remove sellers not in the list of terms so users only see sellers with at least one term
    const availableSellers = sellerList.filter(seller => termSellerMap[seller.id]);

    // Create select options with the remaining sellers
    return availableSellers.map(seller => {
      const { id, name } = seller;
      return {
        value: id,
        label: name
      };
    });
  });

  filterTerms = memoizeOne(
    (terms, cardFilter, termType, selectMultipleTerms, selectedSeller, appliedFilters, filterLogicMapping) => {
      let tableData = selectMultipleTerms
        ? terms.filter(term => term && term.type === termType && term.seller && term.seller.id === selectedSeller)
        : terms;

      if (cardFilter) {
        tableData = terms.filter(
          t => t.displayStatus === cardFilter || (t.previousRevision && t.previousRevision.displayStatus === cardFilter)
        );
      }

      if (!isEmpty(appliedFilters)) {
        tableData = tableData.filter(t =>
          Object.keys(appliedFilters).every(filterName => {
            return filterLogicMapping[filterName](t, appliedFilters[filterName]);
          })
        );
      }

      return tableData;
    }
  );

  onChangeSearch = event => {
    this.props.setHomePageFilterText(event.target.value);
    this.setState({ cardFilter: '' });
  };

  onClickFilterCard = thingToFilter => {
    const cardFilter = this.state.cardFilter;
    this.setState({ cardFilter: cardFilter === thingToFilter ? '' : thingToFilter });
  };

  onClickCreateContract = () => {
    this.props.history.push('create');
  };

  onSellerSelectChange = e => {
    const { onResetTerms, onChangeSeller } = this.props;
    const sellerId = e ? e.value : '';
    // Reset selected terms to prevent selection of terms by multiple sellers at once
    onResetTerms();
    onChangeSeller(sellerId);
  };

  onTermSelectToggle = () => {
    const { selectMultipleTerms, onTermSelectToggle } = this.props;
    onTermSelectToggle(!selectMultipleTerms);
  };

  onTermTypeSelect = (e, type) => {
    const { onChangeType, onResetTerms, terms, authorizedFulfillerNames, sellerId, onChangeSeller } = this.props;

    // If the current selected seller also has terms under the new type, keep that seller selected
    const sellerOptions = this.createFulfillerSelectOptions(terms, authorizedFulfillerNames, type);

    const sameSeller = sellerOptions && sellerOptions.find(seller => seller.value === sellerId);

    const displayedSeller = sellerId && sameSeller ? sameSeller.value : '';

    onChangeType(type);
    onResetTerms();
    onChangeSeller(displayedSeller);
  };

  onBulkApprove = () => {
    const { updateTermStatus, selectedTerms, onResetTerms } = this.props;
    this.setState({ bulkTermStatusUpdating: true });
    const promises = Object.values(selectedTerms)
      .filter(t => t.status === TermStatus.PENDING)
      .map(({ termId, revisionId }) => updateTermStatus(termId, revisionId, TermStatus.APPROVED));
    Promise.all(promises)
      .then(_ => this.setState({ bulkTermStatusUpdating: false }, () => onResetTerms()))
      .catch(_ => this.setState({ bulkTermStatusUpdating: false }, () => onResetTerms()));
  };

  render() {
    const { cardFilter, bulkTermStatusUpdating } = this.state;

    const {
      t,
      authorizedFulfillerNames,
      terms,
      filterText,
      isPageLoading,
      selectedTerms,
      selectMultipleTerms,
      sellerId,
      termType,
      allBuyers,
      allSellers,
      showTerminated,
      appliedFilters,
      filterLogicMapping
    } = this.props;

    const sellerOptions = this.createFulfillerSelectOptions(terms, authorizedFulfillerNames, termType);
    // If there is no currently selected seller, default to the first in the list so the user isn't looking at an empty table
    const selectedSellerId = sellerId || (sellerOptions[0] && sellerOptions[0].value);

    const tableData = this.filterTerms(
      terms,
      cardFilter,
      termType,
      selectMultipleTerms,
      selectedSellerId,
      appliedFilters,
      filterLogicMapping
    );

    const columns = this.createContractColumns({ t, selectMultipleTerms });

    const placeholderText = t('homePage:searchBarPlaceholder');

    const selectedTermList = Object.values(selectedTerms);
    const selectedPendingTermList = selectedTermList.filter(t => t.status === TermStatus.PENDING);

    const multipleTermsSelected = selectedTermList && selectedTermList.length > 0;
    const multiplePendingTermsSelected = selectedPendingTermList.length > 0;

    const radio = selectMultipleTerms ? (
      <div>
        <RadioGroup onChange={this.onTermTypeSelect} valueSelected={termType} name="termSelect">
          <h5>{t('homePage:selectMultipleContracts.contractSelection.contractTypeLabel')}</h5>
          <Radio label={getLabelForContractType(TermType.LIST_PRICE)} value={TermType.LIST_PRICE} />
          <Radio label={getLabelForContractType(TermType.SPECIAL_PRICE)} value={TermType.SPECIAL_PRICE} />
        </RadioGroup>
      </div>
    ) : null;

    const sellerSelect = selectMultipleTerms ? (
      <div className={sellerSelectContainerStyle}>
        <Select
          clearable={false}
          className={sellerSelectStyle}
          options={sellerOptions}
          label={t('homePage:selectMultipleContracts.contractSelection.selectSeller')}
          value={selectedSellerId}
          onChange={this.onSellerSelectChange}
        />
      </div>
    ) : null;

    const bulkUpdateButton = selectMultipleTerms && (
      <div className={updateButtonStyle}>
        <Link to="/bulkupdate">
          <Button type="primary" disabled={!multipleTermsSelected}>
            {t('homePage:selectMultipleContracts.contractSelection.addSKUsTo', {
              count: selectedTermList && selectedTermList.length ? selectedTermList.length : ''
            })}
          </Button>
        </Link>
      </div>
    );
    const bulkApprovalButton = selectMultipleTerms && (
      <div className={updateButtonStyle}>
        <Button
          type="primary"
          onClick={this.onBulkApprove}
          disabled={!multiplePendingTermsSelected || bulkTermStatusUpdating}>
          {`Approve ${
            selectedPendingTermList && selectedPendingTermList.length ? selectedPendingTermList.length : ''
          } Selected Pending Contract(s)`}
        </Button>
      </div>
    );

    return (
      <BreadcrumbsEntry title={{ translationKey: 'breadcrumbs:contracts' }}>
        <BasicLayout className="contract-home">
          <div className="contract-home__header">
            <h1 className="contract-home__header-title">{t('homePage:header.title')}</h1>
            <span className="contract-home__header-btn-container">
              <button
                onClick={this.onClickCreateContract}
                className="btn btn-primary contract-header__create-contract-button">
                {t('common:actions.createNewContract')}
              </button>
            </span>
          </div>
          <TableCards data={terms} filter={cardFilter} onClickFilterCard={this.onClickFilterCard} />
          <div className="contract-home__search">
            <div className="contract-home__search-search-bar">
              <TextField
                id="SearchBar"
                type="search"
                value={filterText}
                onChange={this.onChangeSearch}
                placeholder={placeholderText}
              />
            </div>
          </div>
          <div className={termSelectOptionStyle}>
            {radio}
            {sellerSelect}
            <div className={toggleContainerStyle}>
              <div className={toggleLabelStyle}>{t('homePage:selectMultipleContracts.label')}</div>{' '}
              <Toggle
                on={selectMultipleTerms}
                onClick={this.onTermSelectToggle}
                onText={<span>{t('common:commonWords.onText')}</span>}
                offText={<span>{t('common:commonWords.offText')}</span>}
              />
            </div>
          </div>
          {bulkUpdateButton}
          {bulkApprovalButton}
          <FilterOptions buyers={allBuyers} sellers={allSellers} terms={terms} showTerminated={showTerminated} />
          <Table
            className={tableStyle}
            columns={columns}
            data={tableData}
            loading={isPageLoading}
            showPageSizeOptions={false}
            defaultFilterMethod={(filter, row) => {
              const id = filter.pivotId || filter.id;
              const rowString = String(row[id]).toLowerCase();
              const filterString = filter.value.toLowerCase();
              return row[id] !== undefined ? rowString.includes(filterString) : true;
            }}
            defaultSorted={[
              {
                id: 'isEffective',
                desc: true
              },
              {
                id: 'createdAt',
                desc: true
              }
            ]}
          />
        </BasicLayout>
      </BreadcrumbsEntry>
    );
  }
}

const mapStateToProps = state => ({
  terms: termSelectors.getFilteredTermsForHomePage(state),
  authorizedFulfillerNames: identitySelectors.getAuthorizedFulfillersSortedByName(state),
  authorizedMerchantIds: identitySelectors.getAuthorizedMerchantIds(state),
  filterText: termSelectors.getHomePageFilterText(state),
  isPageLoading: termSelectors.getIsHomePageLoading(state),
  sellerId: state.termSelectReducer.sellerId,
  newTerms: termSelectors.getNewTerms(state),
  allBuyers: termSelectors.getAllContractBuyers(state),
  allSellers: termSelectors.getAllContractSellers(state),
  termType: state.termSelectReducer.termType,
  selectedTerms: state.termSelectReducer.selectedTerms,
  selectMultipleTerms: state.termSelectReducer.selectMultipleTerms,
  updateStatusList: state.termSelectReducer.updateStatusList,
  showTerminated: configSelectors.getShowTerminated(state),
  appliedFilters: homeSelectors.getAppliedFilters(state),
  filterLogicMapping: homeSelectors.getFilterLogicMapping(state)
});

const mapDispatchToProps = {
  fetchTermsForHomePage: termActions.fetchTermsForHomePageV2,
  setHomePageFilterText: termActions.setHomePageFilterText,
  updateTermStatus: termActions.updateTermStatus,
  onChangeType: type => updateTypeDispatch(type),
  onChangeSeller: id => updateSellerIdDispatch(id),
  onResetTerms: resetTermsDispatch,
  onTermSelectToggle: toggleMultiselectDispatch,
  resetSkus: resetSkusDispatch,
  resetState: resetStateDispatch
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(HomePage));
